
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import ServiceHistoryCard from '@/components/dashboard/ServiceHistoryCard';
import BookingStatusTracker from '@/components/booking/BookingStatusTracker';
import { format } from 'date-fns';
import { Calendar, Download } from 'lucide-react';
import {
  ServiceVisit,
  PlantRange,
  ServiceType
} from '@/types';
import { downloadVisitsCalendar } from '@/utils/calendarUtils';

const CustomerDashboard: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  // Mock data - replace with API call in production
  const MOCK_SERVICE_VISITS = [
    {
      id: "visit1",
      userId: "user1",
      technicianId: "tech1",
      technicianName: "John Smith",
      date: new Date(2025, 3, 29),
      status: 'scheduled' as const,
      plantCount: PlantRange.Range2,
      serviceType: ServiceType.Indoor,
    },
    {
      id: "visit2",
      userId: "user1",
      technicianId: "tech1",
      technicianName: "John Smith",
      date: new Date(2025, 3, 22),
      status: 'in-progress' as const,
      plantCount: PlantRange.Range2,
      checkInTime: new Date(2025, 3, 22, 9, 30),
      serviceType: ServiceType.Outdoor,
    },
    {
      id: "visit3",
      userId: "user1",
      technicianId: "tech1",
      technicianName: "John Smith",
      date: new Date(2025, 3, 15),
      status: 'completed' as const,
      plantCount: PlantRange.Range2,
      actualPlantCount: 15,
      checkInTime: new Date(2025, 3, 15, 10, 0),
      checkOutTime: new Date(2025, 3, 15, 10, 45),
      duration: 45,
      notes: "All plants watered and healthy. Recommended more light for the ficus.",
      issues: [
        { type: 'pest' as const, description: 'Spider mites on the monstera', severity: 'medium' as const },
        { type: 'under-watering' as const, description: 'Peace lily was very dry', severity: 'high' as const }
      ],
      serviceType: ServiceType.Indoor,
    },
    {
      id: "visit4",
      userId: "user1",
      technicianId: "tech2",
      technicianName: "Jane Doe",
      date: new Date(2025, 3, 1),
      status: 'completed' as const,
      plantCount: PlantRange.Range2,
      actualPlantCount: 12,
      checkInTime: new Date(2025, 3, 1, 14, 0),
      checkOutTime: new Date(2025, 3, 1, 14, 30),
      duration: 30,
      notes: "All plants watered. Added soil to two pots.",
      issues: [],
      serviceType: ServiceType.Combined,
    }
  ];

  const handleExportAllToCalendar = () => {
    // Export all future visits
    const futureVisits = MOCK_SERVICE_VISITS.filter(
      visit => visit.status === 'scheduled' || visit.status === 'in-progress'
    );
    downloadVisitsCalendar(futureVisits);
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold text-front-gray-900">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container max-w-6xl mx-auto px-4 animate-fade-in">
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-front-gray-900">
                Welcome back, {profile.name}! 🌱
              </h1>
              <p className="text-front-gray-600 mt-2">
                Here's how your plant family is doing.
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/tracking/service')}
                className="hidden md:flex"
              >
                Track Service
              </Button>
              <Button 
                onClick={() => navigate('/booking')}
                className="bg-front-gray-900 hover:bg-front-gray-800 text-white font-semibold"
              >
                Book a Visit
              </Button>
            </div>
          </div>

          {/* Booking Status Tracker - New Major Feature */}
          <BookingStatusTracker />

          <Card className="border-front-gray-200 bg-white shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold text-front-gray-900">
                  Upcoming Service
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 border-front-gray-300 hover:bg-front-gray-50 text-front-gray-700"
                  onClick={handleExportAllToCalendar}
                >
                  <Calendar className="h-4 w-4" />
                  <span className="hidden sm:inline">Export to Calendar</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {MOCK_SERVICE_VISITS.filter(visit => visit.status === 'scheduled').length > 0 ? (
                MOCK_SERVICE_VISITS.filter(visit => visit.status === 'scheduled').map(visit => (
                  <div key={visit.id} className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-front-gray-50 rounded-lg border border-front-gray-200">
                    <div>
                      <p className="text-front-gray-600 text-sm">Date</p>
                      <p className="font-semibold text-front-gray-900">
                        <Calendar className="mr-2 inline-block h-4 w-4" />
                        {format(new Date(visit.date), 'MMMM d, yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className="text-front-gray-600 text-sm">Technician</p>
                      <p className="font-semibold text-front-gray-900">{visit.technicianName}</p>
                    </div>
                    <div>
                      <p className="text-front-gray-600 text-sm">Service Type</p>
                      <p className="font-semibold text-front-gray-900">{visit.serviceType}</p>
                    </div>
                    <div>
                      <p className="text-front-gray-600 text-sm">Plant Count</p>
                      <p className="font-semibold text-front-gray-900">{visit.plantCount}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-front-gray-600">No upcoming services scheduled.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-front-gray-200 bg-white shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-front-gray-900">Service History</CardTitle>
              <CardDescription className="text-front-gray-600">
                View your past services and leave reviews
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] w-full">
                <div className="p-6">
                  {MOCK_SERVICE_VISITS.map((visit) => (
                    <ServiceHistoryCard key={visit.id} visit={visit} />
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
